import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, FileText, ScrollText, Trash2, ChevronRight } from "lucide-react";
import SettingsLayout from "./SettingsLayout";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const PRIVACY_POLICY = [
  "Debii stores the records you enter — the people you lend to, amounts, dates and repayment notes — so you can keep track of them.",
  "Your records are private to your account. We never sell your data, and we don't build public credit scores or borrower profiles.",
  "Acknowledgement links share only the specific record you choose to share, and only with the person you send the link to.",
  "You can delete your account at any time from Account settings, which permanently removes your records.",
];

const TERMS = [
  "Debii is a personal tool for recording money you lend or borrow. It is not a lender, bank, wallet or debt-collection service.",
  "Debii does not provide loans, hold your money, transfer funds, or guarantee that anyone repays you.",
  "You are responsible for the accuracy of the records you enter and for how you use reminders and acknowledgement links.",
  "Debii Plus is an optional annual subscription that unlocks additional features.",
];

export default function PrivacySettings() {
  const navigate = useNavigate();
  const [modal, setModal] = useState(null); // 'policy' | 'terms'

  return (
    <SettingsLayout title="Privacy" subtitle="How Debii handles your data" testId="privacy-settings-page">
      <div className="rounded-2xl bg-primary text-primary-foreground p-5">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-emerald-300" />
          <p className="font-display font-bold">Your data is private</p>
        </div>
        <p className="mt-2 text-sm text-white/85 leading-relaxed">
          Debii helps you privately record and manage money you lend or borrow. Debii does not lend money,
          collect debts on your behalf, or hold your money.
        </p>
      </div>

      <div className="mt-4 rounded-2xl border border-border bg-card divide-y divide-border overflow-hidden">
        <button data-testid="privacy-policy-button" onClick={() => navigate("/privacy-policy")}
          className="w-full flex items-center justify-between px-4 py-4 hover:bg-secondary/50">
          <span className="text-sm text-foreground flex items-center gap-2"><FileText className="h-4 w-4 text-muted-foreground" /> Privacy Policy</span>
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </button>
        <button data-testid="terms-button" onClick={() => navigate("/terms-of-use")}
          className="w-full flex items-center justify-between px-4 py-4 hover:bg-secondary/50">
          <span className="text-sm text-foreground flex items-center gap-2"><ScrollText className="h-4 w-4 text-muted-foreground" /> Terms of Use</span>
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </button>
        <button data-testid="privacy-delete-button" onClick={() => navigate("/settings/account")}
          className="w-full flex items-center justify-between px-4 py-4 hover:bg-secondary/50">
          <span className="text-sm text-red-600 flex items-center gap-2"><Trash2 className="h-4 w-4" /> Delete Account</span>
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </button>
      </div>

      <Dialog open={!!modal} onOpenChange={(o) => !o && setModal(null)}>
        <DialogContent className="rounded-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">
              {modal === "policy" ? "Privacy Policy" : "Terms of Use"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {(modal === "policy" ? PRIVACY_POLICY : TERMS).map((p, i) => (
              <p key={i} className="text-sm text-muted-foreground leading-relaxed">{p}</p>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </SettingsLayout>
  );
}
